import { Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import type { CatalogCategory, CatalogItem } from "@/lib/services-catalog";

export function CatalogCategoryPage({ cat }: { cat: CatalogCategory }) {
  const Icon = cat.icon;
  return (
    <PageShell>
      <PageHero eyebrow="Services" title={cat.title} sub={cat.tagline} />

      <section className="py-8">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <p className="text-lg leading-relaxed text-muted-foreground">{cat.intro}</p>
        </div>
      </section>

      {cat.groups.map((g) => (
        <section key={g.title} className="py-8">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="mb-6 flex items-center gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">{g.title}</h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((it) => (
                <ChildCard key={it.slug} catSlug={cat.slug} item={it} />
              ))}
            </div>
          </div>
        </section>
      ))}

      <CTABanner />
    </PageShell>
  );
}

function ChildCard({ catSlug, item }: { catSlug: string; item: CatalogItem }) {
  const Icon = item.icon;
  return (
    <Link
      to={`/services/${catSlug}/${item.slug}` as never}
      className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-4 rounded-2xl border border-border bg-card p-5 transition hover:border-primary/40 hover:shadow-card"
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <h3 className="truncate text-base font-semibold">{item.title}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.desc}</p>
      </div>
      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
    </Link>
  );
}

export function CatalogChildPage({
  cat, item,
}: { cat: CatalogCategory; item: CatalogItem }) {
  const Icon = item.icon;
  return (
    <PageShell>
      <PageHero
        eyebrow={cat.title}
        title={item.title}
        sub={item.desc}
      />

      <section className="py-8">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <p className="text-lg leading-relaxed text-muted-foreground">
            Our {item.title.toLowerCase()} practice delivers production-ready
            {" "}{cat.title.toLowerCase()} outcomes — designed, built and supported end-to-end
            by senior engineers. {item.desc}
          </p>
        </div>
      </section>

      <section className="py-8">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight">What's included</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              "Discovery & requirements workshop",
              "Solution architecture & data model",
              "Iterative delivery with weekly demos",
              "Integrations with your existing stack",
              "Role-based access & audit trail",
              "Launch support, training & docs",
            ].map((f) => (
              <div key={f} className="rounded-2xl border border-border bg-card p-5">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Sparkles className="h-4 w-4" />
                </div>
                <p className="mt-3 text-sm font-medium">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight">More in {cat.title}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cat.groups.flatMap((g) => g.items).filter((i) => i.slug !== item.slug).slice(0, 6).map((sibling) => (
              <Link
                key={sibling.slug}
                to={`/services/${cat.slug}/${sibling.slug}` as never}
                className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-2xl border border-border bg-card p-4 hover:border-primary/40 transition"
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <sibling.icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold">{sibling.title}</h3>
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{sibling.desc}</p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 pb-8 lg:px-8">
        <Link
          to={`/services/${cat.slug}` as never}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          <ArrowRight className="h-4 w-4 rotate-180" /> Back to {cat.title}
        </Link>
      </div>

      <CTABanner />
    </PageShell>
  );
}
